import { useMemo } from 'react'
import { DragDropContext, type DropResult } from '@hello-pangea/dnd'

import type { Deal, DealStage } from '@/types/deal'
import { DEAL_STAGES } from '@/types/deal'
import { dealsByStage } from '@/features/customers/deal-utils'
import { KanbanColumn } from './kanban-column'

interface KanbanBoardProps {
  deals: Deal[]
  onStageChange: (dealId: string, newStage: DealStage) => void
  onCardEdit?: (deal: Deal) => void
  onCardMessage?: (deal: Deal) => void
}

export function KanbanBoard({
  deals,
  onStageChange,
  onCardEdit,
  onCardMessage,
}: KanbanBoardProps) {
  const grouped = useMemo(() => dealsByStage(deals), [deals])

  // @hello-pangea/dnd's onDragEnd gives us a complete DropResult with
  // source/destination container ids and indices. We only persist
  // cross-column moves (within-column reorders reset on refresh, per
  // the stage-only persistence decision).
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return
    if (source.droppableId === destination.droppableId) return

    onStageChange(result.draggableId, destination.droppableId as DealStage)
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {/*
        Columns have a min width, so on narrow screens the board scrolls
        horizontally instead of crushing the cards. pb-2 keeps drag shadows
        from being clipped by the scroll container.
      */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {DEAL_STAGES.map((stage) => (
          <KanbanColumn
            key={stage}
            stage={stage}
            deals={grouped[stage]}
            onCardEdit={onCardEdit}
            onCardMessage={onCardMessage}
          />
        ))}
      </div>
    </DragDropContext>
  )
}
